/* =========================
   ProductSidebar.jsx (REWRITE)
   - Uses your shared inputs from ../../components
   - Details: key/value rows (value = TextArea)
   - Fixes [object Object] issue by normalizing onChange payloads
   - Phosphor icons only
========================= */

import React, { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { X, FileText, UploadSimple, Trash, Plus } from "phosphor-react"

import { Checkbox, RadioGroup, SelectSearch, TextInput, TextArea } from "../../components"

function ProductSidebar({ categories, brands, done, setDone, onClose, productData }) {
  const [loading, setLoading] = useState(false)
  const [fileChanged, setFileChanged] = useState(false)
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    subcategory_id: "",
    brand_id: "",
    reference: "",
    type: "",
    price: "",
    compare_price: "",
    stock: "",
    sku: "",

    // ✅ key/value details
    details: [{ key: "", value: "" }],

    images: [],
    fiche_technique: null,

    is_top_seller: false,
    is_special_offer: false,
    is_featured: false,
    is_active: true,

    variants: [],
  })

  /* -----------------------------
    1) Helpers
  ------------------------------ */

  const isValidUrl = (url) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  // ✅ Fix [object Object]: handles:
  // - native events
  // - SelectSearch option objects {value,label}
  // - primitive values
  const extractValue = (payload) => {
    if (payload && payload.target) {
      const { type, checked, value } = payload.target
      return type === "checkbox" ? checked : value
    }
    if (payload && typeof payload === "object" && "value" in payload) {
      return payload.value
    }
    return payload
  }

  const setField = (name, payload) => {
    const value = extractValue(payload)
    setFormData((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  // details normalize (supports old html string too)
  const normalizeDetails = (raw) => {
    if (Array.isArray(raw)) return raw.map((d) => ({ key: d?.key || "", value: d?.value || "" }))
    if (typeof raw === "string" && raw.trim()) return [{ key: "Details", value: raw }]
    return [{ key: "", value: "" }]
  }

  /* -----------------------------
    2) Load edit mode
  ------------------------------ */

  useEffect(() => {
    if (!productData) return

    setFormData((prev) => ({
      ...prev,
      ...productData,
      category_id: productData.category_id ?? "",
      subcategory_id: productData.subcategory_id ?? "",
      brand_id: productData.brand_id ?? "",
      images: Array.isArray(productData.images) ? productData.images : [],
      variants: Array.isArray(productData.variants) ? productData.variants : [],
      details: normalizeDetails(productData.details),
      fiche_technique: productData.fiche_technique || null,
    }))

    setFileChanged(false)
    setErrors({})
  }, [productData])

  /* -----------------------------
    3) Options
  ------------------------------ */

  const selectedCategory = useMemo(() => {
    return (categories || []).find((c) => Number(c.id) === Number(formData.category_id))
  }, [categories, formData.category_id])

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories],
  )

  const subcategoryOptions = useMemo(() => {
    const subs = selectedCategory?.subcategories || []
    return subs.map((s) => ({ value: s.id, label: s.name }))
  }, [selectedCategory])

  const brandOptions = useMemo(() => (brands || []).map((b) => ({ value: b.id, label: b.name })), [brands])

  /* -----------------------------
    4) Images
  ------------------------------ */

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const base64Promises = files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
        }),
    )

    Promise.all(base64Promises)
      .then((base64Images) => {
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), ...base64Images],
        }))
      })
      .catch(() => toast.error("Error reading images"))
  }

  const removeImage = (index) => {
    setFormData((prev) => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== index) }))
  }

  /* -----------------------------
    5) PDF
  ------------------------------ */

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== "application/pdf") {
      toast.warning("Veuillez sélectionner un fichier PDF valide.")
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      setField("fiche_technique", reader.result)
      setFileChanged(true)
    }
  }

  const handleRemoveFile = () => {
    setField("fiche_technique", null)
    setFileChanged(false)
  }

  /* -----------------------------
    6) Variants
  ------------------------------ */

  const handleVariantChange = (index, name, payload) => {
    const value = extractValue(payload)
    setFormData((prev) => {
      const variants = [...(prev.variants || [])]
      variants[index] = { ...(variants[index] || {}), [name]: value }
      return { ...prev, variants }
    })
  }

  const addVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [...(prev.variants || []), { name: "", price: "", stock: "", sku: "" }],
    }))
  }

  const removeVariant = (index) => {
    setFormData((prev) => ({ ...prev, variants: (prev.variants || []).filter((_, i) => i !== index) }))
  }

  /* -----------------------------
    7) Details key/value rows
  ------------------------------ */

  const addDetailRow = () => {
    setFormData((prev) => ({
      ...prev,
      details: [...(prev.details || []), { key: "", value: "" }],
    }))
  }

  const removeDetailRow = (index) => {
    setFormData((prev) => {
      const next = (prev.details || []).filter((_, i) => i !== index)
      return { ...prev, details: next.length ? next : [{ key: "", value: "" }] }
    })
  }

  const updateDetailRow = (index, field, payload) => {
    const value = extractValue(payload)
    setFormData((prev) => {
      const next = [...(prev.details || [])]
      next[index] = { ...(next[index] || { key: "", value: "" }), [field]: value }
      return { ...prev, details: next }
    })
  }

  /* -----------------------------
    8) Validate / submit
  ------------------------------ */

  const validate = () => {
    const next = {}

    if (!String(formData.name || "").trim()) next.name = "Required"
    if (!formData.category_id) next.category_id = "Required"
    if (!formData.type) next.type = "Required"
    if (formData.type === "display_price" && !String(formData.price || "").trim()) next.price = "Required"
    if (!String(formData.stock || "").trim()) next.stock = "Required"

    // if value exists -> key required
    ;(formData.details || []).forEach((d, idx) => {
      if (String(d?.value || "").trim() && !String(d?.key || "").trim()) {
        next[`details_${idx}_key`] = "Key required"
      }
    })

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    try {
      const res = productData?.id
        ? await axiosInstance.put(`/products/update/${productData.id}`, formData)
        : await axiosInstance.post("/products/create", formData)

      toast.success(res.data?.message || "Saved")
      onClose()
      setDone(!done)
    } catch (error) {
      toast.error(error.response?.data?.message || "Une erreur est survenue")
    } finally {
      setLoading(false)
    }
  }

  /* -----------------------------
    9) UI
  ------------------------------ */

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {/* overlay */}
        <div onClick={onClose} className="absolute inset-0 bg-black/60" />

        {/* panel */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.25 }}
          className="absolute right-0 top-0 h-full w-full md:w-3/5 bg-white border-l border-neutral-200/70 shadow-2xl overflow-y-auto text-start"
        >
          {/* header */}
          <div className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-neutral-200/70">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-neutral-900 text-white flex items-center justify-center">
                  <FileText size={18} weight="bold" />
                </div>
                <div>
                  <h2 className="text-lg font-[500] text-neutral-900">{productData ? "Update Product" : "Create Product"}</h2>
                  <p className="text-xs text-neutral-500">Clean & consistent with your dashboard theme.</p>
                </div>
              </div>

              <button onClick={onClose} className="p-2 rounded-xl hover:bg-neutral-100 text-neutral-700" type="button">
                <X size={18} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6 bg-neutral-50/60">
            {/* Basic */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <h3 className="text-md font-[500] text-neutral-900 mb-4">Basic information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <TextInput
                  label="Product Name"
                  name="name"
                  value={typeof formData.name === "string" ? formData.name : ""}
                  onChange={(v) => setField("name", v)}
                  error={errors.name}
                  placeholder="e.g. Product Name"
                  required
                />

                <TextInput
                  label="Reference"
                  name="reference"
                  value={typeof formData.reference === "string" ? formData.reference : ""}
                  onChange={(v) => setField("reference", v)}
                  placeholder="e.g. Reference"
                />

                <SelectSearch
                  label="Category"
                  name="category_id"
                  value={formData.category_id}
                  onChange={(v) => {
                    setField("category_id", v)
                    setField("subcategory_id", "")
                  }}
                  options={categoryOptions}
                  error={errors.category_id}
                  placeholder="Select category"
                  isClearable
                />

                <SelectSearch
                  label="Subcategory"
                  name="subcategory_id"
                  value={formData.subcategory_id}
                  onChange={(v) => setField("subcategory_id", v)}
                  options={subcategoryOptions}
                  placeholder="Select subcategory"
                  isClearable
                  isDisabled={!formData.category_id}
                />

                <SelectSearch
                  label="Brand"
                  name="brand_id"
                  value={formData.brand_id}
                  onChange={(v) => setField("brand_id", v)}
                  options={brandOptions}
                  placeholder="Select brand"
                  isClearable
                />

                <RadioGroup
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={(v) => setField("type", v)}
                  error={errors.type}
                  options={[
                    { label: "Demander Devis", value: "demander_devis" },
                    { label: "Display Price", value: "display_price" },
                  ]}
                />
              </div>

              <div className="mt-5">
                <TextArea
                  label="Description"
                  placeholder="e.g. Description"
                  name="description"
                  value={typeof formData.description === "string" ? formData.description : ""}
                  onChange={(v) => setField("description", v)}
                  rows={3}
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <h3 className="text-md font-[500] text-neutral-900 mb-4">Pricing & stock</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <TextInput
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price ?? ""}
                  onChange={(v) => setField("price", v)}
                  error={errors.price}
                  placeholder="e.g. Price"
                />

                <TextInput
                  label="Compare Price"
                  name="compare_price"
                  type="number"
                  value={formData.compare_price ?? ""}
                  onChange={(v) => setField("compare_price", v)}
                  placeholder="e.g. Compare Price"
                />

                <TextInput
                  label="Stock"
                  name="stock"
                  type="number"
                  value={formData.stock ?? ""}
                  onChange={(v) => setField("stock", v)}
                  error={errors.stock}
                  placeholder="e.g. Stock"
                  required
                />

                <TextInput label="SKU" name="sku" value={formData.sku ?? ""} onChange={(v) => setField("sku", v)} />
              </div>
            </div>

            {/* Images */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md font-[500] text-neutral-900">Images</h3>
                <label className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white cursor-pointer hover:opacity-90">
                  <UploadSimple size={16} />
                  Upload
                  <input type="file" multiple onChange={handleImageChange} className="hidden" />
                </label>
              </div>

              {!!formData.images?.length ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative rounded-xl overflow-hidden border border-neutral-200">
                      <img src={img || "/placeholder.svg"} alt="" className="h-24 w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 hover:bg-white border border-neutral-200"
                      >
                        <Trash size={16} className="text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center">
                  <p className="text-sm text-neutral-600">Upload product images.</p>
                  <p className="text-xs text-neutral-500">PNG / JPG recommended.</p>
                </div>
              )}
            </div>

            {/* PDF */}
            {/* <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-md font-[500] text-neutral-900">Fiche technique (PDF)</h3>
                  <p className="text-xs text-neutral-500">Upload a PDF or keep the existing one.</p>
                </div>

                {formData.fiche_technique ? (
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-red-500/10 text-red-700 hover:bg-red-500/15 border border-red-500/20"
                  >
                    <Trash size={16} />
                    Remove
                  </button>
                ) : null}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <input
                  type="file"
                  onChange={handlePdfChange}
                  className="block w-full text-sm text-neutral-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-neutral-900 file:text-white hover:file:opacity-90"
                />

                {formData.fiche_technique && isValidUrl(formData.fiche_technique) && !fileChanged ? (
                  <a
                    href={formData.fiche_technique}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white hover:opacity-90"
                  >
                    View PDF
                  </a>
                ) : null}
              </div>
            </div> */}

            {/* Details key/value */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-[500] text-neutral-900">Details</h3>

                <button
                  type="button"
                  onClick={addDetailRow}
                  className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white hover:bg-cyan-500"
                >
                  <Plus size={16} />
                  Add Detail
                </button>
              </div>

              <div className="space-y-3">
                {(formData.details || []).map((d, idx) => (
                  <div key={idx} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <TextInput
                        label="Key"
                        name={`details_${idx}_key`}
                        value={typeof d?.key === "string" ? d.key : ""}
                        onChange={(v) => updateDetailRow(idx, "key", v)}
                        error={errors[`details_${idx}_key`]}
                        placeholder="e.g. Benefits"
                      />

                      <div className="md:col-span-2">
                        <TextArea
                          label="Value"
                          name={`details_${idx}_value`}
                          value={typeof d?.value === "string" ? d.value : ""}
                          onChange={(v) => updateDetailRow(idx, "value", v)}
                          rows={3}
                          placeholder="e.g. Provides hydration and nourishment to the skin."
                        />
                      </div>
                    </div>

                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={() => removeDetailRow(idx)}
                        className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-red-500/10 text-red-700 hover:bg-red-500/15 border border-red-500/20"
                      >
                        <Trash size={16} />
                        Remove row
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Variants */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-[500] text-neutral-900">Variants</h3>
                <button
                  type="button"
                  onClick={addVariant}
                  className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white hover:bg-cyan-500"
                >
                  <Plus size={16} />
                  Add Variant
                </button>
              </div>

              {(formData.variants || []).length ? (
                <div className="space-y-4">
                  {formData.variants.map((variant, index) => (
                    <div key={index} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextInput
                          label="Variant Name"
                          name={`variant_name_${index}`}
                          value={variant?.name ?? ""}
                          onChange={(v) => handleVariantChange(index, "name", v)}
                          required
                        />
                        <TextInput
                          label="Price"
                          name={`variant_price_${index}`}
                          type="number"
                          value={variant?.price ?? ""}
                          onChange={(v) => handleVariantChange(index, "price", v)}
                        />
                        <TextInput
                          label="Stock"
                          name={`variant_stock_${index}`}
                          type="number"
                          value={variant?.stock ?? ""}
                          onChange={(v) => handleVariantChange(index, "stock", v)}
                          required
                        />
                        <TextInput
                          label="SKU"
                          name={`variant_sku_${index}`}
                          value={variant?.sku ?? ""}
                          onChange={(v) => handleVariantChange(index, "sku", v)}
                        />
                      </div>

                      <div className="flex justify-end mt-3">
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="text-xs font-[500] px-3 py-2 rounded-xl bg-red-500/10 text-red-700 hover:bg-red-500/15 border border-red-500/20"
                        >
                          Remove Variant
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center">
                  <p className="text-sm text-neutral-600">No variants yet.</p>
                </div>
              )}
            </div>

            {/* Flags */}
            <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
              <h3 className="text-md font-[500] text-neutral-900 mb-4">Status & flags</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Checkbox label="Top Seller" checked={!!formData.is_top_seller} onChange={(v) => setField("is_top_seller", v)} />
                <Checkbox label="Special Offer" checked={!!formData.is_special_offer} onChange={(v) => setField("is_special_offer", v)} />
                <Checkbox label="Featured" checked={!!formData.is_featured} onChange={(v) => setField("is_featured", v)} />
                <Checkbox label="Active" checked={!!formData.is_active} onChange={(v) => setField("is_active", v)} />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-neutral-200 bg-white text-sm font-[500] text-neutral-700 hover:bg-neutral-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-xl bg-neutral-900 text-white text-sm font-[500] hover:opacity-90 disabled:opacity-50"
              >
                {loading ? (productData ? "Updating..." : "Creating...") : productData ? "Update Product" : "Create Product"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ProductSidebar
